import React, { useState } from 'react';
import styled from 'styled-components';

import PageHeader from '../../components/PageHeader';
import SearchResultRow from './components/SearchResultRow';
import {
  getPartnersService,
  searchService,
} from '../../services/portalServices';
import type { SearchResult } from '../../client/claims/types.gen';
import Icon from '../../components/Icon';
import { useNotification } from '../../context/notificationContext';

/**
 * A search hit together with the frontend of the partner it came from, so the
 * row can open it in that partner's claims app. (`SearchResult` itself is the
 * claims-server's wire type and carries no partner information.)
 */
export type PartnerSearchResult = {
  frontendBaseUrl: string;
  result: SearchResult;
};

const SearchView = () => {
  const { addNotification, removeNotificationWithDelay } = useNotification();
  const [searchString, setSearchString] = useState('');
  const [searchResult, setSearchResult] = useState<
    Array<PartnerSearchResult> | null
  >(null);

  localStorage.setItem(
    'activeView',
    JSON.stringify({ view: 'search', url: null })
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(event.target.value);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    searchString.trim().length > 0 && getSearchResult(searchString.trim());
  };

  const getSearchResult = async (searchString: string): Promise<void> => {
    const searchResultsArr: PartnerSearchResult[] = [];
    try {
      const partnerUrls = await getPartnersService();
      for (const partnerUrl of partnerUrls) {
        try {
          const results = await searchService(
            partnerUrl.apiBaseUrl,
            searchString
          );
          results.forEach((result) => {
            searchResultsArr.push({
              frontendBaseUrl: partnerUrl.frontendBaseUrl,
              result,
            });
          });
        } catch (err) {
          removeNotificationWithDelay(
            addNotification(
              `Something went wrong with the search for ${partnerUrl.name}. ${
                err instanceof Error ? err.message : String(err)
              }`,
              'alert'
            )
          );
        }
      }
    } catch (err) {
      removeNotificationWithDelay(
        addNotification(
          `Unable to get partner list: ${
            err instanceof Error ? err.message : String(err)
          }`,
          'alert'
        )
      );
    }
    setSearchResult(searchResultsArr);
  };

  return (
    <ContentWrapper>
      <PageHeader />
      <Main>
        <form onSubmit={handleSubmit}>
          <SearchWrapper>
            <SearchInput
              value={searchString}
              onChange={handleChange}
              autoFocus
            />
            <SearchButton type='submit'>
              <Icon iconName='faMagnifyingGlass' color='#fc8e49' />
            </SearchButton>
          </SearchWrapper>
        </form>
        {searchResult && searchResult.length > 0 ? (
          <GridTable>
            <HeaderItem>Claim case</HeaderItem>
            <HeaderItem>Insured object</HeaderItem>
            <HeaderItem>Insured object type</HeaderItem>
            <HeaderItem>Date of incident</HeaderItem>
            <HeaderBorder />
            {searchResult.map((hit, index) => (
              <SearchResultRow key={index} hit={hit} />
            ))}
          </GridTable>
        ) : (
          <span>No result</span>
        )}
      </Main>
    </ContentWrapper>
  );
};

export default SearchView;

const Main = styled.main`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3rem;
  width: 100%;
  margin: 2rem;
`;

const SearchWrapper = styled.div`
  display: flex;
  border-radius: 0.3125rem;
  border: 0.125rem solid #fdc09b;

  &:focus-within {
    border-color: #fda772;
    box-shadow: rgba(252, 142, 73, 0.3) 0 1px 3px 3px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  max-width: 30rem;
  border: none;
  border-top-left-radius: 0.3125rem;
  border-bottom-left-radius: 0.3125rem;
  font-size: 2rem;
  padding: 0.5rem;
`;

const SearchButton = styled.button`
  font-size: 1.5rem;
  border: none;
  background-color: #fff;
  border-top-right-radius: 0.3125rem;
  border-bottom-right-radius: 0.3125rem;
`;

const GridTable = styled.div`
  display: grid;
  grid-template-columns: repeat(4, auto);
  gap: 0.75rem 0.5rem;
  background-color: #fff;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  padding: 0.5rem;
`;

const HeaderItem = styled.div`
  font-weight: 700;
`;

const HeaderBorder = styled.div`
  grid-column: 1 / span 4;
  border-bottom: 0.125rem solid #dedede;
`;
